// AdminHomePage — dashboard trung tâm khi admin login (2026-04-24)
//
// Thay thế cho flow cũ: admin login → HomePage NV → kéo xuống thấy mgmt cards.
// Giờ: admin login → /admin thẳng → thấy grid 14 cards quản lý gom theo nhóm.
// HomePage (`/`) trở thành dành riêng cho NV kiosk.
//
// Group cards (thứ tự ưu tiên — việc hay dùng ở trên, việc ít dùng ở dưới):
//   📚 Nội dung:  Câu hỏi 7 bộ, Nội quy
//   🤖 AI:         Chat AI, Knowledge Base, Dặn dò AI (Prompts)
//   📊 Báo cáo:   Tiến trình NV, Chi phí AI, Tiền thưởng
//   ⚙️ Cài Đặt:    Cài đặt AI, Cấu hình Flashcard, Telegram, Copy động viên
//   👥 Người dùng: Shop, Máy Kiosk, Nhân viên (đẩy xuống cuối — sau setup ít dùng)
//
// Header chỉ còn nút "Đăng xuất" (2026-04-24: đã gỡ nút "👀 Xem UI NV" — bấm bị
// HomeRoute redirect ngược + HomePage NV yêu cầu L2 NV cụ thể, admin không có).

import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';
// M144a (2026-05-12): filter cards theo role/permissions (manager chỉ thấy cards có quyền)
import { hasPermission } from '../lib/permissions.js';

// 2026-04-24: "Người dùng" đẩy xuống CUỐI cùng vì ít dùng (admin ít khi add/sửa
// shop/kiosk/NV sau setup ban đầu). Ưu tiên hiển thị Nội dung + AI + Báo cáo.
const GROUPS = [
  {
    title: '📚 Nội dung đào tạo',
    desc: '6 bộ câu hỏi, nội quy',
    cards: [
      { to: '/admin/questions',     label: 'Câu hỏi (6 bộ)',    icon: '❓', desc: 'CRUD, AI tạo, gộp trùng', perm: 'questions' },
      { to: '/admin/rules-modules', label: 'Nội quy theo chương', icon: '📚', desc: 'Chương + Q&A xen kẽ', perm: 'rules_modules' },
    ],
  },
  {
    title: '🤖 AI & Tri thức',
    desc: 'Chat AI, kiến thức nền, dặn dò',
    cards: [
      { to: '/admin/chat-ai',         label: 'Chat AI (Admin)',    icon: '💬', desc: 'Test KB + dặn dò', perm: 'chat_ai_admin' },
      { to: '/admin/knowledge-base',  label: 'Knowledge Base',     icon: '📘', desc: 'Tài liệu cho RAG', perm: 'knowledge_base' },
      { to: '/admin/prompts',         label: 'Dặn dò AI',         icon: '💡', desc: 'System prompts', perm: 'prompts' },
    ],
  },
  {
    title: '📊 Báo cáo & Phân tích',
    desc: 'Tiến trình NV, chi phí AI, tiền thưởng',
    cards: [
      { to: '/admin/progress', label: 'Theo dõi nhân viên học thẻ', icon: '📈', desc: 'Tổng quan, cảnh báo, xếp hạng', perm: 'progress' },
      { to: '/admin/flashcard-tracking', label: 'Theo dõi ôn tập từng câu', icon: '🔎', desc: 'Lịch sử Quên/Khó/Dễ từng câu + câu cả shop hay quên', perm: 'progress' },
      { to: '/admin/rules-m80', label: 'Theo dõi nhân viên học nội quy', icon: '📚', desc: 'Theo dõi NV học theo vòng + AI on-demand + phao', perm: 'rules_m80' },
      { to: '/admin/flashcard-order', label: 'Thứ tự học theo bộ', icon: '📋', desc: 'Sắp xếp topic NV học trước/sau (3 bộ thưởng)', perm: 'flashcard_order' },
      { to: '/admin/exam-settings', label: 'Xem NV trả lời kiểm tra', icon: '📝', desc: 'Xem câu trả lời + chỉnh ngưỡng đạt', perm: 'exam_settings' },
      { to: '/admin/cost',     label: 'Chi phí AI',    icon: '💰', desc: 'Biểu đồ, top users, lịch sử', perm: 'cost' },
      { to: '/admin/cheat-log', label: 'Log gian lận',  icon: '🚨', desc: 'NV cố tình copy/paste khi học nội quy', perm: 'cheat_log' },
      { to: '/admin/rewards',  label: 'Tiền thưởng NV', icon: '🎁', desc: 'Đánh dấu đã chi tiền NV', requires: 'rewards_enabled', perm: 'rewards' },
    ],
  },
  {
    title: '⚙️ Cài Đặt',
    desc: 'Cấu hình AI, flashcard, Telegram, động viên',
    cards: [
      { to: '/admin/ai',         label: 'Cài đặt AI',        icon: '🛠️', desc: 'Provider, Routing', perm: 'ai_config' },
      { to: '/admin/ai-scope',   label: 'Phạm vi AI',        icon: '🎯', desc: 'Bật/tắt bộ, KB, nội quy, prompts', perm: 'ai_scope' },
      { to: '/admin/ai-tuning',  label: 'Tuning AI',         icon: '🎛', desc: 'Tham số retrieve + preset + model', perm: 'ai_tuning' },
      { to: '/admin/ai-pricing', label: 'Bảng giá AI',       icon: '💰', desc: 'Giá model + tỷ giá USD/VND', perm: 'ai_pricing' },
      { to: '/admin/flashcard',  label: 'Cấu hình Flashcard', icon: '🃏', desc: 'Quota thẻ mới/ôn, SM-2', perm: 'flashcard_config' },
      { to: '/admin/telegram',   label: 'Telegram Bot',      icon: '🔔', desc: 'Alert + recipients', perm: 'telegram' },
      { to: '/admin/motivation', label: 'Copy động viên',   icon: '🎉', desc: '6 event + template', perm: 'motivation' },
      { to: '/admin/announcement', label: 'Thông báo NV',  icon: '📢', desc: 'Popup thông báo khi NV đăng nhập', adminOnly: true },
    ],
  },
  {
    title: '👥 Người dùng',
    desc: 'Quản lý shop, máy kiosk, nhân viên (ít dùng sau setup)',
    cards: [
      { to: '/admin/shops',     label: 'Shop',           icon: '🏪', desc: 'Chi nhánh (HP, HN)', perm: 'shops' },
      // M144a: /admin/users là strict admin-only (không có perm key cho manager → adminOnly: true)
      { to: '/admin/users',     label: 'Máy kiosk',      icon: '💻', desc: 'Tài khoản máy NV', adminOnly: true },
      { to: '/admin/employees', label: 'Nhân viên',      icon: '👤', desc: 'Danh sách NV + TOTP L2', perm: 'employees' },
    ],
  },
];

export default function AdminHomePage() {
  const user = useAuthStore(s => s.user);
  const refreshToken = useAuthStore(s => s.refreshToken);
  const logout = useAuthStore(s => s.logout);
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  // 2026-04-24: Feature flags — bật/tắt Xem thưởng toàn app
  const [features, setFeatures] = useState({ rewards_enabled: true });
  const [togglingRewards, setTogglingRewards] = useState(false);

  useEffect(() => {
    api.health().then(setHealth).catch(() => setHealth(null));
    api.getFeatureFlags().then(setFeatures).catch(() => {});
  }, []);

  async function toggleRewards() {
    if (togglingRewards) return;
    const next = !features.rewards_enabled;
    setTogglingRewards(true);
    try {
      const r = await api.setFeatureFlags({ rewards_enabled: next });
      setFeatures(r);
      toast.success(next ? 'Đã bật Xem thưởng' : 'Đã tắt Xem thưởng');
    } catch (e) {
      toast.error('Lỗi: ' + (e?.data?.error || e?.message));
    } finally {
      setTogglingRewards(false);
    }
  }

  async function doLogout() {
    const ok = await showConfirm({ title: 'Đăng xuất', message: 'Bạn chắc chắn đăng xuất?', confirmText: 'Đăng xuất' });
    if (!ok) return;
    try { await api.logout(refreshToken); } catch {}
    logout();
    toast.success('Đã đăng xuất');
    nav('/login');
  }

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 to-brand-50/30 safe-bottom">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-4 md:py-6">
        {/* M148 (2026-05-12): Header mobile-first — stack vertical, actions full-width row 2 */}
        <header className="mb-5 md:mb-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="text-xs text-slate-500 no-select">Hello Con Đào Tạo · Admin Panel</div>
              <h1 className="text-xl md:text-3xl font-bold text-brand-700 mt-1 leading-tight">
                Xin chào, {user?.display_name || user?.username} 👋
              </h1>
              <div className="text-xs md:text-sm text-slate-500 mt-1">
                Role: <span className="font-mono text-brand-700">{user?.role}</span>
                {health && (
                  <span className="hidden md:inline"> · Server OK · DB {health.db_migrations} migrations · Shop {health.shop_id}</span>
                )}
              </div>
            </div>
          </div>
          {/* Action buttons row — full width mobile, inline desktop */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={toggleRewards}
              disabled={togglingRewards}
              className={`tap-feedback text-sm px-3 py-2 rounded-lg border transition flex-1 sm:flex-none ${
                features.rewards_enabled
                  ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border-slate-300'
              } disabled:opacity-50`}
              title={features.rewards_enabled
                ? 'Đang bật — click để tắt. Khi tắt, NV không thấy "Xem thưởng".'
                : 'Đang tắt — click để bật lại chế độ thưởng.'}
            >
              🎁 Thưởng: <strong className="ml-1">{features.rewards_enabled ? 'BẬT' : 'TẮT'}</strong>
            </button>
            <button
              onClick={doLogout}
              className="tap-feedback text-sm px-3 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 flex-1 sm:flex-none"
            >
              Đăng xuất
            </button>
          </div>
        </header>

        {/* Groups — M144a: filter cards theo permission. M148: grid 2-cols mobile, 3-4 desktop. */}
        <div className="space-y-5 md:space-y-6">
          {GROUPS.map((group) => {
            const visibleCards = group.cards
              .filter(c => !c.requires || features[c.requires])
              .filter(c => {
                if (c.adminOnly) return user?.role === 'admin';
                if (!c.perm) return user?.role === 'admin';
                return hasPermission(user, c.perm);
              });
            if (visibleCards.length === 0) return null;
            return (
              <section key={group.title}>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <h2 className="text-base md:text-lg font-semibold text-slate-800 no-select">{group.title}</h2>
                  <span className="text-xs text-slate-500 hidden sm:inline">{group.desc}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-3">
                  {visibleCards.map((c) => (
                    <Link
                      key={c.to}
                      to={c.to}
                      className="tap-feedback group bg-white rounded-2xl border border-slate-200 p-3.5 md:p-4 hover:border-brand-400 hover:shadow-md transition-all active:bg-slate-50"
                    >
                      <div className="text-2xl mb-1">{c.icon}</div>
                      <div className="font-semibold text-slate-800 group-hover:text-brand-700 text-sm md:text-base leading-tight">
                        {c.label}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{c.desc}</div>
                    </Link>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-8 text-center text-xs text-slate-400">
          Admin Panel · {new Date().toLocaleDateString('vi-VN')} ·
          {' '}<span className="font-mono">Hello Con Đào Tạo</span>
        </div>
      </div>
    </div>
  );
}
